import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from '../../animations/gsap.config';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import homeData from '../../data/home.json';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────
 GALLERY ITEM
───────────────────────────────────────────── */
const GalleryItem = ({ item, isLarge }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Set initial state
      gsap.set(containerRef.current, { opacity: 1 });
      
      // Reveal Entrance
      gsap.fromTo(containerRef.current,
        { clipPath: 'inset(100% 0% 0% 0%)', y: 80 },
        {
          clipPath: 'inset(0% 0% 0% 0%)', 
          y: 0,
          duration: 1.2,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 92%',
          }
        }
      );

      // Scroll Parallax
      if (imgRef.current) {
        gsap.to(imgRef.current, {
          yPercent: 15,
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }
    });
    return () => ctx.revert();
  }, []);

  const handleMouseMove = (e) => {
    const { left, top, width, height } = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;

    gsap.to(innerRef.current, {
      rotateY: x * 8,
      rotateX: -y * 8,
      scale: 1.02,
      duration: 0.5,
      ease: 'power2.out'
    });
  };

  const handleMouseLeave = () => {
    gsap.to(innerRef.current, {
      rotateY: 0, rotateX: 0, scale: 1,
      duration: 0.7, ease: 'power3.out'
    });
  };

  return (
    <div
      ref={containerRef}
      className={`relative rounded-3xl group perspective-1000 ${isLarge ? 'sm:col-span-2 lg:col-span-2' : ''}`}
      style={{ aspectRatio: isLarge ? '16/9' : '4/3' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div ref={innerRef} className="relative w-full h-full rounded-3xl overflow-hidden border border-white/10 will-change-transform transform-gpu shadow-xl">
        <img
          ref={imgRef}
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-[120%] object-cover -top-[10%] transition-transform duration-700 group-hover:scale-110"
        />
        
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-500" />
        
        {/* Floating Tag */}
        <div className="absolute top-5 left-5">
          <span className="text-[9px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full text-white border border-white/10 backdrop-blur-md bg-black/30">
            {item.category}
          </span>
        </div>

        {/* Hover Center Icon - Removed */}

        {/* Text Details */}
        <div className="absolute bottom-0 left-0 right-0 p-8 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
          <h3 className="text-white font-display font-bold text-xl mb-1">{item.title}</h3>
          <p className="text-slate-300 text-xs font-light line-clamp-1 italic">{item.description}</p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN GALLERY SECTION
───────────────────────────────────────────── */
const GallerySection = () => {
  const { gallery } = homeData;
  const [activeFilter, setActiveFilter] = useState('All');
  const [filteredItems, setFilteredItems] = useState(gallery.items);
  
  const sectionRef = useRef(null);
  const gridRef = useRef(null);

  const handleFilter = useCallback((cat) => {
    if (cat === activeFilter) return;
    
    if (gridRef.current && gridRef.current.children) {
      const items = Array.from(gridRef.current.children);
      gsap.to(items, {
        scale: 0.9, 
        opacity: 0, 
        filter: 'blur(10px)',
        stagger: 0.05, 
        duration: 0.3, 
        ease: 'power2.in',
        onComplete: () => {
          setActiveFilter(cat);
          const filtered = cat === 'All' ? gallery.items : gallery.items.filter(i => i.category === cat);
          setFilteredItems(filtered);
        }
      });
    }
  }, [activeFilter, gallery.items]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.children) {
      const items = Array.from(gridRef.current.children);
      gsap.fromTo(items,
        { scale: 0.9, opacity: 0, filter: 'blur(10px)' },
        { scale: 1, opacity: 1, filter: 'blur(0px)', stagger: 0.08, duration: 0.6, ease: 'back.out(1.2)' }
      );
    }
  }, [filteredItems]);

  return (
    <section ref={sectionRef} id="gallery" className="relative overflow-hidden py-32 bg-white">
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-primary-600/5 blur-[120px] rounded-full" />
      </div>

      <div className="container mx-auto relative z-10 px-6 max-w-7xl">
      
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-24">
          <h2 className="font-display font-black text-slate-900 text-5xl md:text-7xl lg:text-8xl mb-6 tracking-tighter leading-none">
            {gallery.headline} <span className="bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">{gallery.headlineAccent}</span>
          </h2>
          <p className="text-slate-600 text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed">
            {gallery.subheadline}
          </p>
        </div>

        {/* Filter Navigation */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-10 mb-16 border-b border-gray-200 pb-10">
          <div className="flex flex-wrap justify-center gap-3">
            {gallery.categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleFilter(cat)}
                className={`px-6 md:px-8 py-2.5 md:py-3 rounded-2xl text-[10px] md:text-[11px] font-black tracking-widest transition-all duration-500 ${
                  activeFilter === cat 
                    ? 'bg-primary-500 text-white shadow-xl shadow-primary-500/30 -translate-y-1' 
                    : 'text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {cat.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="font-mono text-[10px] text-gray-500 tracking-[0.2em] uppercase">
            Curated Collection / <span className="text-gray-900">{filteredItems.length} Projects</span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredItems.map((item, i) => (
            <GalleryItem
              key={item.id}
              item={item}
              isLarge={item.size === 'large'}
            />
          ))}
        </div>

        {/* Footer Hint - Removed */}
      </div>
    </section>
  );
};

export default GallerySection;